import { useState, useEffect } from "react";
import axios from "axios";
import {
    HiOutlineChat,
    HiOutlineClipboardCopy,
    HiCheck,
    HiOutlineSearch,
    HiChevronLeft,
    HiChevronRight,
} from "react-icons/hi";

const ITEMS_PER_PAGE = 10;

export default function SMSHistory({ baseUrl, formatDate }) {
    const [messages, setMessages] = useState([]);
    const [outlets, setOutlets] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const today = new Date().toLocaleDateString('en-CA')
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    const [currentPage, setCurrentPage] = useState(1);
    const [copiedId, setCopiedId] = useState(null);

    const token = localStorage.getItem("token");

    const fetchHistory = async () => {
        if (!token) return;

        setLoading(true);
        setError("");

        try {
            const resp = await axios.post(`${baseUrl}/sms-history`, {
                token,
                start_date: startDate || undefined,
                end_date: endDate || undefined
            });

            if (resp.data.success) {
                setMessages(resp.data.system_entries || []);
                setOutlets(resp.data.outlets || []);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to load SMS history");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtering (search only)
    const filteredMessages = messages.filter((msg) => {
        const text = msg.message_text?.toLowerCase() || "";
        return text.includes(searchTerm.toLowerCase());
    });

    // Pagination
    const totalPages = Math.ceil(filteredMessages.length / ITEMS_PER_PAGE);
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedMessages = filteredMessages.slice(
        startIdx,
        startIdx + ITEMS_PER_PAGE
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Copy SMS text
    const copyText = async (msg) => {
        try {
            await navigator.clipboard.writeText(msg.message_text || "");
            setCopiedId(msg.id);
            setTimeout(() => setCopiedId(null), 4000);
        } catch (err) {
            console.error("Copy failed", err);
        }
    };

    if (loading) {
        return (
            <div className="py-10 text-center text-sm text-gray-400">
                Loading SMS history…
            </div>
        );
    }
    if (error) {
        return <div className="py-6 text-center text-sm text-red-600">{error}</div>;
    }

    return (
        <div>
            {/* Date Range Filter */}
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div className="relative flex-1">
                    <HiOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search SMS text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    />
                </div>

                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 sm:w-40"
                />

                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 sm:w-40"
                />

                <button
                    onClick={fetchHistory}
                    className="px-4 py-1.5 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    View
                </button>
            </div>

            {/* Outlet Summary Table */}
            {outlets.length > 0 && (
                <div className="mb-4">
                    <h3 className="text-[12.5px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Outlet Summary
                    </h3>

                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full max-w-md text-[12.5px]">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-1.5 text-left font-medium text-gray-500">
                                        Outlet
                                    </th>
                                    <th className="px-3 py-1.5 text-left font-medium text-gray-500">
                                        Sent
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {outlets.map((outlet, index) => (
                                    <tr key={index}>
                                        <td className="px-3 py-1.5 text-gray-700">
                                            {outlet.sender_name}
                                        </td>
                                        <td className="px-3 py-1.5 font-semibold text-gray-800">
                                            {outlet.total_numbers}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* SMS History Cards */}
            {filteredMessages.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">No SMS records found.</p>
            ) : (
                <>
                    <div className="space-y-2.5">
                        {paginatedMessages.map((msg) => (
                            <div
                                key={msg.id}
                                className="border border-gray-200 rounded-lg px-3 py-2.5 bg-white"
                            >
                                <div className="flex items-start gap-4">
                                    <HiOutlineChat className="text-indigo-500 w-4 h-4 mt-0.5 shrink-0" />

                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                                            <span className="text-[13px] font-medium text-gray-800">
                                                {formatDate(msg.created_at)}
                                            </span>
                                            <span className="text-[12px] text-gray-500">
                                                Sent to{" "}
                                                <span className="font-semibold text-gray-700">
                                                    {msg.total_numbers}
                                                </span>{" "}
                                                numbers
                                            </span>
                                        </div>

                                        <div className="mt-1.5 text-[13px] text-gray-700 whitespace-pre-wrap">
                                            {msg.message_text}
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => copyText(msg)}
                                        className="shrink-0 flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded-md hover:bg-indigo-50 transition-colors"
                                    >
                                        {copiedId === msg.id ? (
                                            <>
                                                <HiCheck className="text-green-600 w-3.5 h-3.5" /> Copied
                                            </>
                                        ) : (
                                            <>
                                                <HiOutlineClipboardCopy className="w-3.5 h-3.5" /> Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-end items-center gap-1.5 mt-3">
                        <button
                            onClick={() =>
                                setCurrentPage((p) => Math.max(1, p - 1))
                            }
                            disabled={currentPage === 1}
                            className={`flex items-center gap-0.5 px-2.5 py-1 text-[12.5px] rounded-md border transition-colors
                                ${currentPage === 1
                                    ? "text-gray-300 border-gray-200"
                                    : "text-indigo-600 border-gray-200 hover:bg-indigo-50"}`}
                        >
                            <HiChevronLeft className="w-3.5 h-3.5" /> Prev
                        </button>

                        <span className="text-[12.5px] text-gray-500 px-1">
                            {currentPage} / {totalPages || 1}
                        </span>

                        <button
                            onClick={() =>
                                setCurrentPage((p) =>
                                    Math.min(totalPages, p + 1)
                                )
                            }
                            disabled={currentPage === totalPages}
                            className={`flex items-center gap-0.5 px-2.5 py-1 text-[12.5px] rounded-md border transition-colors
                                ${currentPage === totalPages
                                    ? "text-gray-300 border-gray-200"
                                    : "text-indigo-600 border-gray-200 hover:bg-indigo-50"}`}
                        >
                            Next <HiChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}